/**
 * components/BackupHealthAlert.tsx
 * ------------------------------------------------------------------
 * Full-screen, admin-only alert that fires when the automatic backups
 * are unhealthy — either the backup Cloud Function wrote healthy:false
 * (a caught failure) OR no successful backup has landed in >26h (a hard
 * OOM/timeout that couldn't even write the failure). For years this
 * failed SILENTLY; now an admin cannot miss it.
 *
 * Signal: the `reportBackupHealth/status` sentinel doc, written by
 * functions/backups.js. Read is admin/head-gated by Firestore rules, so
 * we only subscribe for those roles (others get permission-denied).
 *
 * Dismissible but persistent: closing it hides it for this session; it
 * re-shows on the next login (the component remounts, state resets) and
 * immediately re-shows if a NEWER failure arrives.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertTriangle, Database, X } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

/** No successful backup in this long ⇒ treat as unhealthy even if the
 *  sentinel still says healthy (covers a kill that couldn't write). */
const STALE_MS = 26 * 60 * 60 * 1000;
const ADMIN_ROLES = ['admin', 'systemAdmin', 'headteacher'];

interface BackupHealth {
  healthy?: boolean;
  lastSuccessAt?: string;
  lastFailureAt?: string;
  lastError?: string | null;
  lastRunType?: string;
}

export default function BackupHealthAlert() {
  const { userRoles } = useAuth();
  const isAdminHead = (userRoles ?? []).some((r) => ADMIN_ROLES.includes(r));
  const [health, setHealth] = useState<BackupHealth | null>(null);
  const [dismissedSig, setDismissedSig] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdminHead) return;
    const unsub = onSnapshot(
      doc(db, 'reportBackupHealth', 'status'),
      (snap) => setHealth(snap.exists() ? (snap.data() as BackupHealth) : null),
      () => setHealth(null), // permission or transient error — stay quiet
    );
    return unsub;
  }, [isAdminHead]);

  if (!isAdminHead || !health) return null;

  const lastSuccessMs = health.lastSuccessAt ? Date.parse(health.lastSuccessAt) : 0;
  const stale = !lastSuccessMs || Date.now() - lastSuccessMs > STALE_MS;
  const unhealthy = health.healthy === false || stale;
  if (!unhealthy) return null;

  // Signature changes when a newer failure lands ⇒ re-show after dismiss.
  const sig = `${health.lastFailureAt ?? ''}|${health.lastSuccessAt ?? ''}|${health.healthy}`;
  if (dismissedSig === sig) return null;

  const lastSuccessLabel = health.lastSuccessAt
    ? new Date(health.lastSuccessAt).toLocaleString()
    : 'never recorded';

  return (
    <div className="fixed inset-0 z-[115] bg-[#0e1830]/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-rose-600 to-rose-800 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-xl ring-1 ring-white/30 flex items-center justify-center">
              <AlertTriangle size={22} />
            </div>
            <div>
              <p className="font-bold text-lg">Automatic backup failed</p>
              <p className="text-white/80 text-xs">Your data is not being safely backed up</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {health.healthy === false
              ? 'The most recent backup did not complete.'
              : `No successful backup has been recorded in over ${Math.round(STALE_MS / 3600000)} hours.`}
            {' '}This usually means the backup ran out of memory or storage.
          </p>

          <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-3 text-xs space-y-1">
            <div className="flex justify-between gap-3">
              <span className="text-gray-400">Last successful backup</span>
              <span className="font-medium text-gray-700 dark:text-gray-200">{lastSuccessLabel}</span>
            </div>
            {health.lastError && (
              <div className="pt-1">
                <span className="text-gray-400 block">Error</span>
                <span className="font-mono text-[11px] text-rose-600 dark:text-rose-400 break-words">{health.lastError}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => { window.location.hash = '#/audit'; setDismissedSig(sig); }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-rose-600 to-rose-700 text-white font-semibold text-sm hover:scale-[1.02] transition-transform"
            >
              <Database size={16} /> Open Data &amp; Backups
            </button>
            <button
              onClick={() => setDismissedSig(sig)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <X size={16} /> Dismiss
            </button>
          </div>
          <p className="text-[11px] text-gray-400">
            Dismissing hides this until your next sign-in. It will keep appearing until a backup succeeds.
          </p>
        </div>
      </div>
    </div>
  );
}
